import * as React from 'react';
import { useHistory } from 'react-router-dom';
import { IconButton } from '@material-ui/core';
import ArrowBack from '#root/icons/ArrowBack';
import { cva, type VariantProps } from 'class-variance-authority';

import styles from './page-header.module.scss';

// ============================================================================
// Breadcrumb Types
// ============================================================================

export interface BreadcrumbItem {
  label: string;
  href?: string;
  onClick?: () => void;
}

// ============================================================================
// Breadcrumb Component
// ============================================================================

export interface BreadcrumbProps {
  items: BreadcrumbItem[];
  separator?: React.ReactNode;
}

export const Breadcrumb = React.forwardRef<HTMLElement, BreadcrumbProps>(
  ({ items, separator = '/' }, ref) => {
    const history = useHistory();

    const handleClick = (item: BreadcrumbItem, e: React.MouseEvent) => {
      if (item.onClick) {
        e.preventDefault();
        item.onClick();
      } else if (item.href) {
        e.preventDefault();
        history.push(item.href);
      }
    };

    return (
      <nav ref={ref} className={styles.breadcrumb} aria-label="Breadcrumb">
        <ol className={styles.breadcrumbList}>
          {items.map((item, index) => {
            const isLast = index === items.length - 1;

            return (
              <li key={index} className={styles.breadcrumbItem}>
                {!isLast && (item.href || item.onClick) ? (
                  <a
                    href={item.href || '#'}
                    onClick={(e) => handleClick(item, e)}
                    className={styles.breadcrumbLink}>
                    {item.label}
                  </a>
                ) : (
                  <span
                    className={
                      isLast ? styles.breadcrumbCurrent : styles.breadcrumbText
                    }
                    aria-current={isLast ? 'page' : undefined}>
                    {item.label}
                  </span>
                )}

                {!isLast && (
                  <span className={styles.breadcrumbSeparator} aria-hidden="true">
                    {separator}
                  </span>
                )}
              </li>
            );
          })}
        </ol>
      </nav>
    );
  }
);

Breadcrumb.displayName = 'Breadcrumb';

// ============================================================================
// PageHeader Variants (CVA)
// ============================================================================

const pageHeaderVariants = cva(styles.pageHeader, {
  variants: {
    size: {
      sm: styles.sm,
      md: styles.md,
      lg: styles.lg,
    },
  },
  defaultVariants: {
    size: 'md',
  },
});

// ============================================================================
// PageHeader Component
// ============================================================================

export interface PageHeaderProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'>,
    VariantProps<typeof pageHeaderVariants> {
  title: React.ReactNode | string;
  subtitle?: React.ReactNode | string;
  extra?: React.ReactNode | React.ReactNode[] | null;
  backButton?: boolean;
  onBack?: () => void;
  breadcrumb?: BreadcrumbItem[];
  breadcrumbSeparator?: React.ReactNode;
}

export const PageHeader = React.forwardRef<HTMLDivElement, PageHeaderProps>(
  (
    {
      title,
      subtitle,
      extra,
      backButton = false,
      onBack,
      breadcrumb,
      breadcrumbSeparator,
      size,
      className,
      ...props
    },
    ref
  ) => {
    const history = useHistory();

    const handleGoBack = () => {
      if (onBack) {
        onBack();
      } else {
        history.goBack();
      }
    };

    return (
      <div
        ref={ref}
        className={`${pageHeaderVariants({ size })} ${className || ''}`}
        {...props}>
        {/* Breadcrumb (Solo desktop) */}
        {breadcrumb && breadcrumb.length > 0 && (
          <div className={styles.breadcrumbWrapper}>
            <Breadcrumb items={breadcrumb} separator={breadcrumbSeparator} />
          </div>
        )}

        {/* Header Content */}
        <div className={styles.headerContent}>
          {/* Back Button + Title + Subtitle */}
          <div className={styles.headingWrapper}>
            {backButton && (
              <IconButton
                color="secondary"
                size="small"
                onClick={handleGoBack}
                className={styles.backButton}
                aria-label="Volver">
                <ArrowBack />
              </IconButton>
            )}

            <div className={styles.headingBox}>
              <h2 className={styles.title}>{title}</h2>
              {subtitle && <span className={styles.subtitle}>{subtitle}</span>}
            </div>
          </div>

          {/* Extra Actions */}
          {extra && (
            <div className={styles.actions}>
              {Array.isArray(extra) ? (
                extra.map((element, index) => (
                  <React.Fragment key={index}>{element}</React.Fragment>
                ))
              ) : (
                <>{extra}</>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }
);

PageHeader.displayName = 'PageHeader';

export { pageHeaderVariants };
